import { Component,
  EventEmitter,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  ViewChild,
  OnDestroy,
  OnInit, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { UploadFilesService } from './upload-files.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  buildHeaderDataShell,
} from '../../../shared/workspace-element-context';

export interface FileListItem {
  name: string;
  isFolder: boolean;
  showConfirmation?: boolean;
  isDeleting?: boolean;
}

@Component({
    selector: 'gtw-efile-upload-files',
    templateUrl: './upload-files.component.html',
    styleUrls: ['./upload-files.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class UploadFilesComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private isInitialized = false;
  private isLoading = false;

  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  rowData: any = {};
  filterParams: any = {};

  files: File[] = [];
  listPath: string = '';
  uploading: boolean = false;
  loadingFiles: boolean = false;
  minimise: boolean = false;
  list: FileListItem[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private gtwSnackbarService: GtwSnackbarService,
    private uploadFilesService: UploadFilesService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext())
    );
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext())
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = gc;
    this.baseURLs = {
      api: gc.api,
      admin: gc.admin,
      custom: gc.custom,
      efile: gc.efile,
      pdfEngine: gc.pdfEngine,
      bulkPdfEngine: gc.bulkPdfEngine,
    };
    const hdr = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.USER_SETTINGS = hdr.userSettingsData;
    this.SERVER_CONFIG = hdr.serverConfigData;
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.markForCheck();
    this.tryInit();
  }

  private tryInit(): void {
    if (this.isInitialized) {
      return;
    }
    if (!this.baseURLs?.efile) {
      return;
    }
    this.isInitialized = true;
    this.viewFiles();
  }

  /** Non-empty server message when API returns callSuccess !== 1 with a usable errorMessage. */
  private serverErrorMessageFromResponse(response: any): string {
    const raw = response?.errorMessage ?? response?.error_message;
    if (raw == null) {
      return '';
    }
    const s = String(raw).trim();
    if (!s || s.toLowerCase() === 'null') {
      return '';
    }
    return s;
  }

  upload(): void {
    if (this.files.length === 0) {
      return;
    }
    this.uploading = true;
    this.cdr.detectChanges();

    const efileApiUrl = this.baseURLs.efile;
    this.uploadFilesService.uploadFiles(efileApiUrl, this.listPath, this.files).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response: any) => {
        this.uploading = false;
        const cs = response?.callSuccess;
        const ok =
          cs === undefined || cs === null
            ? true
            : cs === '1' || cs === 1;
        if (ok) {
          this.files = [];
          if (this.fileInput?.nativeElement) {
            this.fileInput.nativeElement.value = '';
          }
          this.viewFiles();
          this.gtwSnackbarService.openSnackBar('Files uploaded successfully.', 'gtw-snackbar--green');
        } else {
          const serverMsg = this.serverErrorMessageFromResponse(response);
          this.gtwSnackbarService.openSnackBar(
            serverMsg || 'Failed to upload files. Please try again.',
            'gtw-snackbar--red'
          );
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.uploading = false;
        this.gtwSnackbarService.openSnackBar('Failed to upload files. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    });
  }

  folderClicked(file: FileListItem): void {
    if (!file.isFolder) {
      return;
    }
    this.listPath = this.listPath ? `${this.listPath}/${file.name}` : file.name;
    this.viewFiles();
  }

  goBack(): void {
    const parts = this.listPath.split('/');
    parts.pop();
    this.listPath = parts.join('/');
    this.viewFiles();
  }

  deleteFile(file: FileListItem): void {
    const pathToDelete = this.listPath ? `${this.listPath}/${file.name}` : file.name;
    file.isDeleting = true;
    this.cdr.detectChanges();

    this.uploadFilesService.deleteFile(this.baseURLs.efile, pathToDelete).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.viewFiles();
        this.gtwSnackbarService.openSnackBar('File deleted successfully.', 'gtw-snackbar--green');
        this.cdr.detectChanges();
      },
      error: () => {
        file.isDeleting = false;
        this.gtwSnackbarService.openSnackBar('Failed to delete file. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input?.files) {
      this.files = Array.from(input.files);
      this.cdr.detectChanges();
    }
  }

  /** Comma-separated names for the label beside the file input (single line + ellipsis). */
  get selectedFilesSummary(): string {
    if (!this.files?.length) {
      return '';
    }
    return this.files.map((f) => f.name).join(', ');
  }

  /** Full list on hover when names are truncated. */
  get filesTitleTooltip(): string {
    return this.files?.map((f) => f.name).join('\n') ?? '';
  }

  viewFiles(): void {
    if (!this.baseURLs?.efile) {
      return;
    }
    this.loadingFiles = true;
    this.cdr.detectChanges();

    this.uploadFilesService.viewFiles(this.baseURLs.efile, this.listPath).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response: any) => {
        this.list = response?.data?.files || response?.files || [];
        this.loadingFiles = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.list = [];
        this.loadingFiles = false;
        this.gtwSnackbarService.openSnackBar('Failed to load files.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    });
  }

  cancel(): void {
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true,
    });
    document.querySelector('gtw-efile-upload-files-ce')?.dispatchEvent(updateEvent);
  }
}
